import { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  ChevronLeft,
  ChevronRight,
  Lightbulb,
  Gem,
  Hammer,
  Sparkles,
  Star,
  ArrowRight,
  Sofa,
  Bed,
  Utensils,
  DoorOpen,
  Briefcase,
  AlignVerticalSpaceBetween,
  X,
  Maximize2,
} from "lucide-react";
import { useReveal } from "../hooks/useReveal";
import FlyWords from "./FlyWords";

/* ------------------------------------------------------------------ */
/*  Tier data — extracted from the four tier flyers                    */
/* ------------------------------------------------------------------ */
const TIERS = [
  {
    n: 1,
    name: "Premium Wall",
    tagline: "Transform any wall into a luxury statement.",
    description:
      "A sleek, modern finish that instantly elevates your space with high-end slat materials and flawless craftsmanship.",
    flyer:
      "https://customer-assets-agu9un31.emergentagent.net/job_luxury-walls-preview/artifacts/nnza7v59_796354291_1780418799758870_4858807738862307886_n.jpg",
    features: [
      { icon: Gem, title: "Premium Materials", desc: "A variety of slat finishes and colors." },
      { icon: Sparkles, title: "Sleek Modern Design", desc: "Adds depth, texture and elegance to any room." },
      { icon: Hammer, title: "Expert Installation", desc: "Precision built for a perfect, seamless fit." },
      { icon: Star, title: "Built to Impress", desc: "High-end look. Long-lasting quality. Every time." },
    ],
    accent: "#C79A2E",
  },
  {
    n: 2,
    name: "Premier Wall + Integrated Lighting",
    tagline: "Luxury that looks even better after dark.",
    description:
      "The perfect balance of design and illumination — premium materials and integrated LED lighting turn your wall into a stunning centerpiece.",
    flyer:
      "https://customer-assets-agu9un31.emergentagent.net/job_luxury-walls-preview/artifacts/qx4lkhmr_770640914_2279552206198979_128212892358313831_n.jpg",
    features: [
      { icon: Lightbulb, title: "Integrated LED Lighting", desc: "Built directly into the design for a seamless, high-end finish." },
      { icon: Gem, title: "Premium Materials", desc: "Hand-selected slats and premium panels for a modern look." },
      { icon: Hammer, title: "Expert Craftsmanship", desc: "Precision installation with clean lines and flawless detailing." },
      { icon: Sparkles, title: "Custom Ambiance", desc: "Control the mood with lighting that highlights your style." },
    ],
    accent: "#D4AF37",
  },
  {
    n: 3,
    name: "Premier Wall + Integrated Lighting",
    tagline: "The upgraded centerpiece — style, depth and illumination.",
    description:
      "Builds on Tier 2 with layered LED lighting that enhances your centerpiece and creates the perfect ambiance day and night.",
    flyer:
      "https://customer-assets-agu9un31.emergentagent.net/job_luxury-walls-preview/artifacts/eidlcvg5_799501820_950869934052320_4616448585088601457_n.jpg",
    features: [
      { icon: Lightbulb, title: "Integrated LED Lighting", desc: "Seamless, high-end finish that enhances any space." },
      { icon: Gem, title: "Premium Materials", desc: "Hand-selected slats and panels for a rich, lasting look." },
      { icon: Hammer, title: "Expert Craftsmanship", desc: "Precision installation with flawless attention to detail." },
      { icon: Sparkles, title: "Custom Ambiance", desc: "Create the perfect mood that elevates your space." },
    ],
    accent: "#E5C158",
  },
  {
    n: 4,
    name: "Premier Wall + Integrated Lighting",
    tagline: "The pinnacle of custom wall design.",
    description:
      "Layered materials, built-in lighting, stone and refined finishes create a stunning high-end look with unmatched ambiance and sophistication.",
    flyer:
      "https://customer-assets-agu9un31.emergentagent.net/job_luxury-walls-preview/artifacts/5qie7x5l_796581337_1644873974087786_2351055424289471612_n.jpg",
    features: [
      { icon: Lightbulb, title: "Integrated LED Lighting", desc: "Built directly into the design for a seamless, high-end finish." },
      { icon: Gem, title: "Premium Materials", desc: "Slats, stone, marble or custom finishes for a luxurious look." },
      { icon: Hammer, title: "Expert Craftsmanship", desc: "Precision installation with clean lines and flawless detail." },
      { icon: Sparkles, title: "Custom Ambiance", desc: "Perfect mood lighting that elevates every space." },
    ],
    accent: "#F3E5AB",
  },
];

const PERFECT_FOR = [
  { icon: Sofa, label: "Living rooms" },
  { icon: Bed, label: "Bedrooms" },
  { icon: Utensils, label: "Dining rooms" },
  { icon: DoorOpen, label: "Entryways" },
  { icon: Briefcase, label: "Offices" },
  { icon: AlignVerticalSpaceBetween, label: "Accent walls" },
];

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */
export default function Tiers() {
  const ref = useReveal();
  const trackRef = useRef(null);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [zoom, setZoom] = useState(null); // tier flyer opened in lightbox

  const scrollToIndex = useCallback((i) => {
    const track = trackRef.current;
    if (!track) return;
    const cards = track.querySelectorAll("[data-tier-card]");
    if (!cards[i]) return;
    track.scrollTo({ left: cards[i].offsetLeft - track.offsetLeft, behavior: "smooth" });
    setIndex(i);
  }, []);

  const next = useCallback(() => {
    const ni = (index + 1) % TIERS.length;
    scrollToIndex(ni);
  }, [index, scrollToIndex]);

  const prev = useCallback(() => {
    const ni = (index - 1 + TIERS.length) % TIERS.length;
    scrollToIndex(ni);
  }, [index, scrollToIndex]);

  // Auto-advance every 5s (pauses on hover)
  useEffect(() => {
    if (paused) return;
    const t = setInterval(next, 5000);
    return () => clearInterval(t);
  }, [next, paused]);

  // Keep `index` in sync when user swipes/drags the track manually
  const onTrackScroll = () => {
    const track = trackRef.current;
    if (!track) return;
    const cards = track.querySelectorAll("[data-tier-card]");
    let closest = 0;
    let min = Infinity;
    cards.forEach((c, i) => {
      const d = Math.abs(c.offsetLeft - track.offsetLeft - track.scrollLeft);
      if (d < min) {
        min = d;
        closest = i;
      }
    });
    if (closest !== index) setIndex(closest);
  };

  return (
    <section
      id="tiers"
      data-testid="tiers-section"
      className="section bg-[#050505] relative"
      ref={ref}
    >
      <div className="container-x">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10 reveal">
          <div className="max-w-2xl">
            <div className="overline mb-5">Our tiers</div>
            <FlyWords
              as="h2"
              className="font-display text-4xl md:text-5xl lg:text-6xl text-white font-light leading-tight"
              segments={[
                { text: "Four tiers." },
                { type: "break" },
                { text: "One promise: built to impress.", className: "italic text-[#D4AF37]" },
              ]}
            />
            <p className="mt-5 text-base md:text-lg text-[#A1A1AA]">
              From a sleek slat feature to a fully layered media wall with integrated lighting — pick the
              tier that fits, we'll tailor the rest.
            </p>
          </div>

          {/* Arrow controls */}
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={prev}
              data-testid="tiers-prev"
              aria-label="Previous tier"
              className="h-11 w-11 rounded-full border border-[#27272A] text-white hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors flex items-center justify-center"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={next}
              data-testid="tiers-next"
              aria-label="Next tier"
              className="h-11 w-11 rounded-full border border-[#27272A] text-white hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors flex items-center justify-center"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Track */}
        <div
          className="relative reveal"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          <div
            ref={trackRef}
            onScroll={onTrackScroll}
            className="flex gap-5 md:gap-6 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-2 no-scrollbar"
            style={{ scrollbarWidth: "none" }}
            data-testid="tiers-track"
          >
            {TIERS.map((t, i) => (
              <TierCard key={t.n} tier={t} active={i === index} onZoom={() => setZoom(t)} />
            ))}
          </div>

          {/* Edge fades */}
          <div className="pointer-events-none absolute inset-y-0 left-0 w-10 bg-gradient-to-r from-[#050505] to-transparent hidden md:block" />
          <div className="pointer-events-none absolute inset-y-0 right-0 w-10 bg-gradient-to-l from-[#050505] to-transparent hidden md:block" />
        </div>

        {/* Dots */}
        <div className="flex justify-center gap-2 mt-6" data-testid="tiers-dots">
          {TIERS.map((t, i) => (
            <button
              key={t.n}
              type="button"
              onClick={() => scrollToIndex(i)}
              aria-label={`Go to Tier ${t.n}`}
              data-testid={`tiers-dot-${i}`}
              className={`h-1.5 rounded-full transition-all ${
                i === index ? "w-8 bg-[#D4AF37]" : "w-2 bg-[#3f3f46] hover:bg-[#6b6b72]"
              }`}
            />
          ))}
        </div>
      </div>

      {/* Lightbox */}
      {zoom && (
        <div
          data-testid="tier-lightbox"
          className="fixed inset-0 z-[80] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setZoom(null)}
        >
          <button
            data-testid="tier-lightbox-close"
            className="absolute top-5 right-5 p-2 text-white/80 hover:text-[#D4AF37]"
            onClick={() => setZoom(null)}
            aria-label="Close"
          >
            <X className="h-7 w-7" />
          </button>
          <img
            src={zoom.flyer}
            alt={`Tier ${zoom.n} — ${zoom.name}`}
            className="max-h-[90vh] max-w-[92vw] w-auto h-auto object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Card                                                               */
/* ------------------------------------------------------------------ */
function TierCard({ tier, active, onZoom }) {
  return (
    <article
      data-tier-card
      data-testid={`tier-card-${tier.n}`}
      className={`snap-start flex-none w-[88%] sm:w-[70%] md:w-[58%] lg:w-[46%] xl:w-[38%] rounded-2xl border transition-all duration-500 overflow-hidden bg-gradient-to-br from-[#0d0d0d] via-[#0a0a0a] to-[#050505] ${
        active ? "border-[#D4AF37]/70 shadow-[0_0_60px_rgba(212,175,55,0.15)]" : "border-[#27272A]"
      }`}
    >
      {/* Flyer image — contains the 4 project photos + wall showcase */}
      <button
        type="button"
        onClick={onZoom}
        data-testid={`tier-flyer-${tier.n}`}
        className="block w-full text-left relative group bg-black"
        aria-label={`Expand Tier ${tier.n} details`}
      >
        <img
          src={tier.flyer}
          alt={`Tier ${tier.n} — ${tier.name} · four project photos and wall showcase`}
          loading="lazy"
          className="w-full h-auto object-contain"
        />
        <div className="absolute top-3 right-3 h-9 w-9 rounded-full bg-black/60 border border-white/20 flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity">
          <Maximize2 className="h-4 w-4" />
        </div>
      </button>

      {/* Header strip */}
      <div className="relative px-6 pt-6 pb-5 border-b border-[#1a1a1a] flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] tracking-[0.32em] uppercase text-[#A1A1AA]">
            Swift Luxury Walls
          </div>
          <div className="mt-2 font-display text-2xl md:text-3xl text-white leading-tight">
            {tier.name}
          </div>
          <div
            className="mt-2 text-sm italic"
            style={{ color: tier.accent }}
          >
            {tier.tagline}
          </div>
        </div>
        <div
          className="flex-none rounded-lg border px-3 py-1.5 text-center min-w-[64px]"
          style={{ borderColor: `${tier.accent}66`, background: `${tier.accent}0d` }}
        >
          <div className="text-[9px] tracking-[0.28em] uppercase" style={{ color: tier.accent }}>
            Tier
          </div>
          <div className="font-display text-2xl leading-none" style={{ color: tier.accent }}>
            {tier.n}
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="px-6 py-6">
        <p className="text-sm text-[#D1D1D6] leading-relaxed">{tier.description}</p>

        <div className="mt-6 grid grid-cols-2 gap-4">
          {tier.features.map((f, i) => {
            const Icon = f.icon;
            return (
              <div key={i} className="flex items-start gap-3">
                <div
                  className="flex-none h-9 w-9 rounded-full border flex items-center justify-center"
                  style={{ borderColor: `${tier.accent}55`, color: tier.accent }}
                >
                  <Icon className="h-4 w-4" />
                </div>
                <div>
                  <div className="text-[13px] font-medium text-white leading-tight">{f.title}</div>
                  <div className="text-[11px] text-[#A1A1AA] mt-1 leading-snug">{f.desc}</div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Perfect for */}
        <div className="mt-6 pt-5 border-t border-[#1a1a1a]">
          <div className="text-[10px] tracking-[0.28em] uppercase text-[#A1A1AA] mb-3">
            Perfect for
          </div>
          <div className="flex flex-wrap gap-2">
            {PERFECT_FOR.map((p, i) => {
              const Icon = p.icon;
              return (
                <span
                  key={i}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-[#27272A] text-[11px] text-[#D1D1D6]"
                >
                  <Icon className="h-3 w-3 text-[#D4AF37]" />
                  {p.label}
                </span>
              );
            })}
          </div>
        </div>

        {/* CTA */}
        <Link
          to="/design-my-wall"
          data-testid={`tier-cta-${tier.n}`}
          className="mt-6 w-full btn-gold !py-3 justify-center"
        >
          Design a Tier {tier.n} wall <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </article>
  );
}
